import React, { useState, useContext, Fragment } from 'react';
import { List, Button } from 'antd-mobile';
import { CheckOutline } from 'antd-mobile-icons';
import styled from 'styled-components';
import uniq from 'lodash/uniq';
import { SIGNATURE_TYPE_TASK } from '../utils/constants';
import { sign, unsign } from '../api';
import { matchesPermission } from '../utils/permission-helpers';
import { getSignatureTypes, getUserAllowedSignatureTypes } from '../utils/helpers';
import AddSignaturePopup from './AddSignaturePopup';
import UserSelfContext from '../utils/self-context';

const EmptyText = styled.span`
	color: var(--adm-color-weak);
`;

const ButtonWrapper = styled.div`
	padding: 8px;
	margin-top: 12px;
`;

const Required = styled.span`
	display: inline-block;
	margin-left: 2px;
	color: #f5222d;
	font-size: 14px;
	line-height: 1;
`;


function Signatures(props) {
	const {
		item,
		objectType,
		onChange,
		isDisabled,
		actionText,
		doneText,
		emptyText
	} = props;
	const [saving, setSaving] = useState('');
	const { userSelf } = useContext(UserSelfContext);
	const [showPopover, setShowPopover] = useState(false);
	const [isRemove, setIsRemove] = useState(false);
	const [selectedSignature, setSelectedSignature] = useState({});

	const disabled = isDisabled || !matchesPermission(userSelf, 'add_signatures_work_orders');

	const SIGNATURE_TYPES = getSignatureTypes(userSelf, SIGNATURE_TYPE_TASK);

	const userAllowedSignatures = getUserAllowedSignatureTypes(userSelf, SIGNATURE_TYPE_TASK, false);

	const allowedSignatures = userAllowedSignatures
		.map(st => st.id)
		.filter(id => !(item.signatures || []).find(s => s.signature_type_id === id && s.user_id === userSelf.id && s.object_type === objectType));

	async function signSelf(signatureId) {
		setSaving(signatureId);
		try {
			await sign({
				object_type: objectType,
				signature_type_id: signatureId,
				object_id: item.id
			});
			await onChange(item.id);
			setShowPopover(false);
			setSaving('');
		} catch (err) {
			setSaving('');
		}
	}

	async function unsignSelf(signatureId) {
		setSaving(true);
		try {
			await unsign({ id: signatureId });

			await onChange(item.id, true);
			setShowPopover(false);
			setSaving('');
		} catch (err) {
			await onChange(item.id, true);
			setShowPopover(false);
			setSaving(false);
		}
	}

	function getSignaturesByType(typeId) {
		return item.signatures.filter(s => s.signature_type_id === typeId && s.object_type === objectType) || [];
	}

	function isRequired(typeId) {
		return item.required_signature_ids ? item.required_signature_ids.includes(typeId) : false;
	}

	function getGroupName(id) {
		const type = SIGNATURE_TYPES.find(t => t.id === id);

		return type ? type.name : 'Unknown';
	}

	function hasSignature(typeId) {
		return getSignaturesByType(typeId).some(s => s.user_id === userSelf.id);
	}

	function onSignatureClick(typeId) {
		const userSignature = getSignaturesByType(typeId).find(s => s.user_id === userSelf.id);
		if (userSignature) {
			setSelectedSignature(userSignature);
			setIsRemove(true);
			setShowPopover(true);
		}
	}

	const signatureTypes = uniq([...(item.required_signature_ids || []), ...(item.signatures?.filter(s => s.object_type === objectType) || []).map(s => s.signature_type_id)]);

	function onMarkAsComplete() {
		if (userAllowedSignatures.length > 1) {
			setIsRemove(false);
			setShowPopover(true);
		} else {
			signSelf(userAllowedSignatures[0].id);
		}
	}

	return (<div>
		<List header={doneText || 'Completed by'}>
			{signatureTypes.map((typeId) => {
				return <List.Item
					key={typeId}
					title={<Fragment>
						{getGroupName(typeId)}
						{isRequired(typeId) ? <Required>*</Required> : null}
					</Fragment>}
					clickable={hasSignature(typeId) && !disabled}
					onClick={() => hasSignature(typeId) && !disabled && onSignatureClick(typeId)}
				>	{!getSignaturesByType(typeId).length && <EmptyText>{emptyText || 'Not completed yet...'}</EmptyText>}
					{getSignaturesByType(typeId).map(s => s.signature_data.name).join(', ')}
				</List.Item>;
			})}
			{!signatureTypes.length && <List.Item><EmptyText>{emptyText || 'Not completed yet...'}</EmptyText></List.Item>}
		</List>
		{!!allowedSignatures.length && !disabled && !!userAllowedSignatures.length && <ButtonWrapper>
			<Button loading={saving} onClick={onMarkAsComplete} color={'success'} block>
				<CheckOutline /> {actionText || 'Mark as Completed'}
			</Button>
		</ButtonWrapper>}
		<AddSignaturePopup
			visible={showPopover}
			signatureId={selectedSignature.id}
			userAllowedSignatures={userAllowedSignatures}
			allowedSignatures={allowedSignatures}
			signatureName={selectedSignature.signature_name}
			isRemove={isRemove}
			saving={saving}
			onSelect={signSelf}
			onDelete={unsignSelf}
			onClose={() => {
				setShowPopover(false);
				setSelectedSignature({});
				setIsRemove(false);
			}}
		/>
	</div>);
}

export default Signatures;
